/**
 * NAVI Corpus — dev/preview training upload.
 *
 * Anyone can upload during preview testing. Items get fed back into Claude 4.5
 * as reference context at brief-generation and scam-check time.
 *
 * Route: /dev/corpus
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Upload, FileText, Trash2, Filter, AlertTriangle, ShieldAlert, FileCheck, Scale, HelpCircle, Loader2, ThumbsUp, ThumbsDown, Quote, PlayCircle } from "lucide-react";
import VideoEmbed from "@/components/VideoEmbed";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const NAVY = "#0A1F44";
const NAVY_DARK = "#061633";
const GOLD = "#C9A24A";

const KIND_META = {
  product_disclosure: { label: "Product disclosure", icon: FileCheck,  color: "#2563EB" },
  product_quote:      { label: "Product quote",       icon: FileText,   color: "#0891B2" },
  scam_sample:        { label: "Scam sample",         icon: ShieldAlert, color: "#DC2626" },
  regulatory:         { label: "Regulatory",          icon: Scale,      color: "#7C3AED" },
  training_video:     { label: "Training video",      icon: PlayCircle, color: "#059669" },
  other:              { label: "Other",               icon: HelpCircle, color: "#64748B" },
};

const PRODUCT_TYPES = [
  "life_insurance", "funeral_cover", "term_life", "credit_life",
  "retirement_annuity", "unit_trust", "endowment", "living_annuity",
  "home_loan", "vehicle_finance", "personal_loan", "credit_card", "overdraft",
  "medical_aid", "hospital_plan", "gap_cover",
  "short_term_insurance", "cellphone_insurance",
];

const SCAM_TYPES = [
  "phishing", "smishing", "sim_swap", "romance", "investment_scam", "419_advance_fee",
  "bank_impersonation", "sars_impersonation", "courier_delivery", "employment_scam",
  "prize_lottery", "crypto_scam",
];

export default function NaviCorpusPage() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState({ kind: "", product_type: "", scam_type: "" });
  const [reloadKey, setReloadKey] = useState(0);
  const [form, setForm] = useState({
    kind: "product_disclosure", title: "", product_type: "", scam_type: "",
    tags: "", notes: "", file: null,
    video_url: "", duration_seconds: "",
  });
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  useEffect(() => {
    const params = new URLSearchParams();
    Object.entries(filter).forEach(([k, v]) => v && params.set(k, v));
    axios.get(`${API}/corpus${params.toString() ? `?${params}` : ""}`)
      .then(r => setItems(r.data || []))
      .catch(() => setItems([]));
  }, [filter, reloadKey]);

  const submit = async (e) => {
    e.preventDefault();
    setUploadError("");
    const isVideo = form.kind === "training_video";
    if (isVideo && !form.video_url.trim()) { setUploadError("Video URL required."); return; }
    if (!isVideo && !form.file) { setUploadError("Pick a file first."); return; }
    const fd = new FormData();
    if (!isVideo) fd.append("file", form.file);
    fd.append("kind", form.kind);
    if (form.title)        fd.append("title", form.title);
    if (form.product_type) fd.append("product_type", form.product_type);
    if (form.scam_type)    fd.append("scam_type", form.scam_type);
    if (form.tags)         fd.append("tags", form.tags);
    if (form.notes)        fd.append("notes", form.notes);
    if (isVideo) {
      fd.append("video_url", form.video_url.trim());
      if (form.duration_seconds) fd.append("duration_seconds", String(form.duration_seconds));
    }
    setUploading(true);
    try {
      await axios.post(`${API}/corpus`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ kind: form.kind, title: "", product_type: "", scam_type: "", tags: "", notes: "", file: null, video_url: "", duration_seconds: "" });
      setReloadKey(k => k + 1);
    } catch (err) {
      setUploadError(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove from NAVI corpus?")) return;
    try {
      await axios.delete(`${API}/corpus/${id}`);
      setReloadKey(k => k + 1);
    } catch (err) {
      alert(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "#F8F9FA" }} data-testid="corpus-page">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 py-5 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Dev / preview</div>
            <h1 className="font-display text-2xl font-semibold" style={{ color: NAVY }}>NAVI training corpus</h1>
            <p className="text-xs text-slate-500 mt-0.5">Upload real disclosures, quotes and scam samples. NAVI uses them as reference at brief-generation and scam-check time.</p>
          </div>
          <Link to="/wallet" className="text-sm font-medium hover:underline" style={{ color: NAVY }} data-testid="back-to-wallet">← Wallet</Link>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-8 grid lg:grid-cols-[380px_1fr] gap-8">
        {/* ─── Upload form ─── */}
        <aside>
          <form onSubmit={submit} className="rounded-2xl bg-white border border-slate-100 p-5 shadow-sm space-y-3" data-testid="corpus-upload-form">
            <div className="font-display text-lg font-semibold" style={{ color: NAVY }}>Upload</div>

            <FormField label="Kind">
              <select value={form.kind} onChange={e => setForm({ ...form, kind: e.target.value })} data-testid="corpus-kind" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
                {Object.entries(KIND_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
              </select>
            </FormField>

            <FormField label="Title">
              <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="corpus-title" placeholder="e.g. Sanlam Term Life Disclosure v3" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            </FormField>

            {(form.kind === "product_disclosure" || form.kind === "product_quote" || form.kind === "training_video") && (
              <FormField label="Product type">
                <select value={form.product_type} onChange={e => setForm({ ...form, product_type: e.target.value })} data-testid="corpus-product-type" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
                  <option value="">— any —</option>
                  {PRODUCT_TYPES.map(p => <option key={p} value={p}>{p.replace(/_/g, " ")}</option>)}
                </select>
              </FormField>
            )}

            {form.kind === "scam_sample" && (
              <FormField label="Scam type">
                <select value={form.scam_type} onChange={e => setForm({ ...form, scam_type: e.target.value })} data-testid="corpus-scam-type" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
                  <option value="">— any —</option>
                  {SCAM_TYPES.map(p => <option key={p} value={p}>{p.replace(/_/g, " ")}</option>)}
                </select>
              </FormField>
            )}

            <FormField label="Tags (comma-separated)">
              <input value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} data-testid="corpus-tags" placeholder="sms, bank_impersonation" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            </FormField>

            <FormField label="Notes (context for NAVI)">
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="corpus-notes" placeholder="e.g. Reported by pensioner in Cape Town, Nov 2025" rows={3} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            </FormField>

            <FormField label="File (max 10 MB)">
              <input type="file" onChange={e => setForm({ ...form, file: e.target.files?.[0] || null })} data-testid="corpus-file" className="w-full text-sm" disabled={form.kind === "training_video"} />
              {form.kind === "training_video" && <div className="text-[10px] text-slate-400 mt-1">Not needed for training videos — paste URL above.</div>}
            </FormField>

            {form.kind === "training_video" && (
              <>
                <FormField label="Video URL (YouTube / Vimeo / Loom / MP4)">
                  <input value={form.video_url} onChange={e => setForm({ ...form, video_url: e.target.value })} data-testid="corpus-video-url" placeholder="https://www.youtube.com/watch?v=…" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
                </FormField>
                <FormField label="Duration (seconds, optional)">
                  <input type="number" min={0} value={form.duration_seconds} onChange={e => setForm({ ...form, duration_seconds: e.target.value })} data-testid="corpus-duration" placeholder="180" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
                </FormField>
              </>
            )}

            {uploadError && (
              <div className="rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-800 p-2 flex gap-1.5 items-start">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>{uploadError}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={uploading || !form.file}
              data-testid="corpus-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition disabled:opacity-40"
              style={{ background: NAVY, color: "white" }}
            >
              {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4" /> Upload to NAVI</>}
            </button>
          </form>
        </aside>

        {/* ─── List + filters ─── */}
        <main>
          <div className="flex items-center gap-3 mb-4">
            <Filter className="w-4 h-4 text-slate-400" />
            <FilterSelect value={filter.kind} onChange={v => setFilter({ ...filter, kind: v })} testid="filter-kind" placeholder="all kinds">
              {Object.entries(KIND_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
            </FilterSelect>
            <FilterSelect value={filter.product_type} onChange={v => setFilter({ ...filter, product_type: v })} testid="filter-product" placeholder="any product">
              {PRODUCT_TYPES.map(p => <option key={p} value={p}>{p.replace(/_/g, " ")}</option>)}
            </FilterSelect>
            <FilterSelect value={filter.scam_type} onChange={v => setFilter({ ...filter, scam_type: v })} testid="filter-scam" placeholder="any scam type">
              {SCAM_TYPES.map(p => <option key={p} value={p}>{p.replace(/_/g, " ")}</option>)}
            </FilterSelect>
            <div className="text-[11px] text-slate-500 ml-auto">
              {items === null ? "loading…" : `${items.length} item${items.length === 1 ? "" : "s"}`}
            </div>
          </div>

          {items === null ? (
            <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading corpus…</div>
          ) : items.length === 0 ? (
            <EmptyCorpus />
          ) : (
            <div className="space-y-2" data-testid="corpus-list">
              {items.map(it => <CorpusRow key={it.id} item={it} onDelete={() => remove(it.id)} />)}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function FilterSelect({ value, onChange, placeholder, testid, children }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} data-testid={testid} className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs">
      <option value="">{placeholder}</option>
      {children}
    </select>
  );
}

function CorpusRow({ item, onDelete }) {
  const meta = KIND_META[item.kind] || KIND_META.other;
  const Icon = meta.icon;
  const cited = item.cite_count || 0;
  const up = item.helpful_count || 0;
  const down = item.not_helpful_count || 0;
  const totalFb = up + down;
  const score = totalFb > 0 ? Math.round((up / totalFb) * 100) : null;
  return (
    <div data-testid={`corpus-row-${item.id}`} className="flex gap-3 rounded-xl bg-white border border-slate-100 p-4 hover:shadow-sm transition">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${meta.color}18` }}>
        <Icon className="w-4 h-4" style={{ color: meta.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: meta.color }}>{meta.label}</span>
          {item.product_type && <span className="text-[10px] uppercase tracking-widest text-slate-400">· {item.product_type.replace(/_/g, " ")}</span>}
          {item.scam_type && <span className="text-[10px] uppercase tracking-widest text-slate-400">· {item.scam_type.replace(/_/g, " ")}</span>}
        </div>
        <div className="mt-0.5 text-sm font-semibold text-slate-800 truncate">{item.title}</div>
        <div className="text-[11px] text-slate-500 truncate">
          {item.kind === "training_video"
            ? <>{item.video_url} {item.duration_seconds ? `· ${Math.floor(item.duration_seconds / 60)}:${String(item.duration_seconds % 60).padStart(2, "0")}` : ""}</>
            : <>{item.filename} · {prettyBytes(item.size_bytes)}</>
          }
          {" · "}{timeAgo(item.uploaded_at)}
        </div>

        {/* ═══ Quality signal row ═══ */}
        <div className="mt-2 flex items-center gap-3 text-[11px]" data-testid={`corpus-stats-${item.id}`}>
          <span className="inline-flex items-center gap-1 text-slate-600" title="Times NAVI was shown this item">
            <Quote className="w-3 h-3" /> cited {cited}
          </span>
          {up > 0 && (
            <span className="inline-flex items-center gap-1 text-emerald-700" title="Helpful votes on briefs/checks that used this item">
              <ThumbsUp className="w-3 h-3" /> {up}
            </span>
          )}
          {down > 0 && (
            <span className="inline-flex items-center gap-1 text-rose-700" title="Not-helpful votes on briefs/checks that used this item">
              <ThumbsDown className="w-3 h-3" /> {down}
            </span>
          )}
          {score !== null && (
            <span className={`ml-auto text-[10px] font-mono px-1.5 py-0.5 rounded ${score >= 70 ? "bg-emerald-50 text-emerald-700" : score >= 40 ? "bg-amber-50 text-amber-700" : "bg-rose-50 text-rose-700"}`}>
              score {score}%
            </span>
          )}
        </div>

        {(item.tags || []).length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {item.tags.map(t => <span key={t} className="text-[10px] rounded-full bg-slate-100 text-slate-600 px-2 py-0.5">{t}</span>)}
          </div>
        )}
        {item.notes && <div className="text-[11px] text-slate-600 mt-1 italic">&ldquo;{item.notes}&rdquo;</div>}
        {item.kind === "training_video" && item.video_url && (
          <details className="mt-2">
            <summary className="text-[11px] text-slate-500 cursor-pointer hover:underline">Preview video</summary>
            <div className="mt-2 max-w-md"><VideoEmbed url={item.video_url} title={item.title} duration_seconds={item.duration_seconds} /></div>
          </details>
        )}
        {item.text_extract && item.kind !== "training_video" && (
          <details className="mt-2">
            <summary className="text-[11px] text-slate-500 cursor-pointer hover:underline">Preview extracted text</summary>
            <pre className="mt-1 text-[11px] text-slate-600 bg-slate-50 rounded p-2 whitespace-pre-wrap max-h-40 overflow-auto">{item.text_extract.slice(0, 1200)}{item.text_extract.length > 1200 ? "\n…" : ""}</pre>
          </details>
        )}
      </div>
      <button onClick={onDelete} data-testid={`corpus-delete-${item.id}`} className="text-slate-400 hover:text-rose-600 transition flex-shrink-0" aria-label="Delete corpus item">
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

function EmptyCorpus() {
  return (
    <div className="rounded-2xl bg-white border border-slate-100 p-10 text-center" data-testid="corpus-empty">
      <FileText className="w-8 h-8 text-slate-300 mx-auto" />
      <div className="mt-3 font-display text-lg font-semibold" style={{ color: NAVY }}>No items yet</div>
      <p className="text-sm text-slate-600 mt-1 max-w-md mx-auto">
        Upload real disclosures, quotes or scam samples on the left. NAVI will use them as reference context on every brief and every scam check.
      </p>
    </div>
  );
}

function prettyBytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
  return `${Math.floor(diff / 86400)} d ago`;
}
